// @flow
import React from 'react';
import { graphql, compose } from 'react-apollo';
import { memoize } from 'lodash';
import update from 'immutability-helper';

import Button from 'components/ButtonDeprecated';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator';
import ConfirmDialog from 'components/ConfirmDialog';
import ErrorReporter from 'app/components/ErrorReporter';
import EventTracking from 'lib/EventTracking';
import Heading from 'components/Heading';
import MediaObject from 'components/MediaObject';
import ModalTrigger from 'components/ModalTrigger';
import StudentIdListTable from 'chat/components/StudentIdListTable';
import TargetByStudentIdSelectPageRole from './TargetByStudentIdSelectPageRole';
import Text from 'components/Text';
import ToastActions from 'actions/ToastActions';

import PencilImage from 'assets/images/pencil.svg';
import HeartImage from 'assets/images/heart.svg';

import GET_STUDENT_ID_LIST_QUERY from '../queries/GetStudentIdListQuery.gql';
import PUT_STUDENT_ID_LIST_MUTATION from '../queries/PutStudentIdListMutation.gql';

import type { GetStudentIdList } from '../queries/__generated__/GetStudentIdList';

type Props = {
  studentIdListUuid: string,
  csvName: string,
  orgUuid: string,
  onContinue: (studentIdList: *, csvName: string) => void,
  onMount: () => void,
  onBack: () => void,

  // gql data
  data: ReactApolloData<GetStudentIdList>,

  // graphql mutations
  mutate: ({
    variables: {
      input: {
        uuid: string,
        fileUuid: string,
        organizationUuid: string,
        includeParents?: boolean,
        includeStudents?: boolean,
      },
    },
  }) => Promise<*>,
};

const prefixEvent = (suffix: string) =>
  `announcements.target_by_student_id.list_of_uploaded_users.${suffix}`;
function trackEvent(key: string, properties?: Object = {}) {
  EventTracking.trackKey(prefixEvent(key), properties);
}

class TargetByStudentIdSelectPage extends React.Component<Props> {
  componentDidMount() {
    this.props.onMount();
    trackEvent('view');
  }

  toggleRoleInclusion = (
    roleToToggle: 'parent' | 'student',
    studentIdListUuid: string,
    fileUuid: string,
    organizationUuid: string,
    includeParents: boolean,
    includeStudents: boolean
  ) => {
    if (roleToToggle === 'parent') {
      const event = includeParents ? 'parent_checkbox.select' : 'parent_checkbox.unselect';
      trackEvent(event);
    }

    if (roleToToggle === 'student') {
      const event = includeStudents ? 'student_checkbox.select' : 'student_checkbox.unselect';
      trackEvent(event);
    }

    const handleError = (error: Error, consoleError?: string) => {
      ToastActions.showErrorToast(error.message);
      console.warn(error);
      if (consoleError) {
        console.error(consoleError);
      }
    };
    this.props
      .mutate({
        variables: {
          input: {
            uuid: studentIdListUuid,
            fileUuid,
            organizationUuid,
            includeParents,
            includeStudents,
          },
        },
        optimisticResponse: {
          __typename: 'Mutation',
          putStudentIdList: {
            __typename: 'PutStudentIdListPayload',
            results: {
              __typename: 'StudentIdList',
              uuid: studentIdListUuid,
              fileUuid,
              includeParents,
              includeStudents,
            },
            error: null,
          },
        },
      })
      .then(
        response => {
          const { error } = response.data.putStudentIdList;
          if (error) {
            handleError(error);
          }
        },
        error =>
          handleError(
            error,
            `TBSI - putStudentIdList error while attempting to toggle ${roleToToggle} inclusion`
          )
      )
      .catch(error =>
        handleError(
          error,
          `TBSI - putStudentIdList runtime error while attempting to toggle ${roleToToggle} inclusion`
        )
      );
  };

  loadMoreStudents = memoize<[string], void>((cursor: string) => {
    this.props.data.fetchMore({
      variables: {
        uuid: this.props.studentIdListUuid,
        cursor,
        limit: 20,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return previousResult;
        }

        const existingStudents = previousResult.studentIdList.matched.edges;
        const newStudents = fetchMoreResult.studentIdList.matched.edges.slice(1);

        if (newStudents.length <= 0) {
          return previousResult;
        }

        return update(previousResult, {
          studentIdList: {
            matched: {
              pageInfo: {
                $set: fetchMoreResult.studentIdList.matched.pageInfo,
              },
              edges: { $set: [...existingStudents, ...newStudents] },
            },
          },
        });
      },
    });
  });

  render() {
    const { data, csvName } = this.props;

    if (data && data.loading && !data.studentIdList) {
      return <CenteredLoadingIndicator />;
    }

    if (!data.studentIdList) {
      ToastActions.showErrorToast(
        'Looks like something went wrong - please email support@remindhq.com for further assistance.'
      );
      this.props.onBack();
      return (
        <ErrorReporter error="TBSI - tried to render TargetByStudentIdSelectPage without a student ID list" />
      );
    }

    const {
      uuid,
      fileUuid,
      organizationUuid,
      includeStudents,
      includeParents,
      parentsCount,
      studentsCount,
      studentsWithoutParentsCount,
    } = data.studentIdList;

    const totalCount = (includeParents ? parentsCount : 0) + (includeStudents ? studentsCount : 0);

    return (
      <React.Fragment>
        <div className="TargetByStudentIdSelectPage-main">
          <StudentIdListTable
            studentIdList={data.studentIdList}
            loading={data.loading}
            orgUuid={this.props.orgUuid}
            onLoadMoreStudents={this.loadMoreStudents}
            onParentAdd={() => {
              this.loadMoreStudents.cache.clear();
              this.props.data.refetch();
            }}
            eventKeys={{
              unidentifiedStudentsHelpCTA: prefixEvent('unidentified_students.help_cta.click'),
              unidentifiedStudentsExpand: prefixEvent('unidentified_students.expand'),
              unidentifiedStudentsCollapse: prefixEvent('unidentified_students.collapse'),
            }}
          />
        </div>
        <div className="TargetByStudentIdSelectPage-sidebar">
          <MediaObject figure="right" center className="TargetByStudentIdSelectPage-sidebar-header">
            <Heading level="4">Selected</Heading>
            <Heading level="4">{totalCount}</Heading>
          </MediaObject>
          <TargetByStudentIdSelectPageRole
            onClick={() => {
              this.toggleRoleInclusion(
                'student',
                uuid,
                fileUuid,
                organizationUuid,
                includeParents,
                !includeStudents
              );
            }}
            icon={PencilImage}
            roleType="student"
            personCount={studentsCount}
            checked={includeStudents}
            enabled
          />
          <TargetByStudentIdSelectPageRole
            onClick={() => {
              this.toggleRoleInclusion(
                'parent',
                uuid,
                fileUuid,
                organizationUuid,
                !includeParents,
                includeStudents
              );
            }}
            icon={HeartImage}
            roleType="parent"
            personCount={parentsCount}
            checked={includeParents}
            enabled={!!parentsCount}
            orphanCount={studentsWithoutParentsCount}
          />
        </div>
        <div className="TargetByStudentIdSelectPage-footer">
          <ModalTrigger
            modal={
              <ConfirmDialog
                title="Are you sure you want to go back?"
                onConfirm={this.props.onBack}
                confirmEventKey={prefixEvent('go_back_dialog.go_back.click')}
                cancelEventKey={prefixEvent('go_back_dialog.cancel.click')}>
                <Text color="graphite">Your uploaded list of student numbers will be cleared.</Text>
              </ConfirmDialog>
            }>
            <Button level="secondary" data-test="back-button" eventKey={prefixEvent('back.click')}>
              Back
            </Button>
          </ModalTrigger>
          <Button
            onClick={() => this.props.onContinue(data.studentIdList, csvName)}
            disabled={!includeParents && !includeStudents}
            data-test="continue-button"
            eventKey={prefixEvent('continue.click')}>
            Continue
          </Button>
        </div>
      </React.Fragment>
    );
  }
}

const withStudentIdList = graphql(GET_STUDENT_ID_LIST_QUERY, {
  options: ({ studentIdListUuid }) => ({
    variables: {
      uuid: studentIdListUuid,
      limit: 20,
    },
    notifyOnNetworkStatusChange: true,
  }),
});

const withMutation = graphql(PUT_STUDENT_ID_LIST_MUTATION);

export { TargetByStudentIdSelectPage };

export default compose(withStudentIdList, withMutation)(TargetByStudentIdSelectPage);
